import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Button } from "../../shadcn-components/ui/button";
import DataTableFacetedFilter from "../common/DataTableFacetedFilter";
import { useFetchFilters } from "../../hooks/useFetchFilters";
import baseUrl from "../../config/baseUrl";
import { CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../shadcn-components/ui/popover";
// import { Calendar } from "../../shadcn-components/ui/calendar";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import getAllAssignUsers from "../../api/getAllAssignUsers";

const DataToolbar = ({
  table,
  apiUrl,
  clientID,
  clientTypeOptions,
  createdAt,
  updatedAt,
  setCreatedAt,
  setUpdatedAt,
  createdAtLabel,
  UpdatedAtLabel,
}) => {
  const clientCategories =
    Array.isArray(clientTypeOptions) && clientTypeOptions.length > 0
      ? clientTypeOptions.map((item) => item.label)
      : [];

  const { id, is_admin, token } = useContext(AuthContext);
  const [category, setCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [clientCategoryandSubCategory, setClientCategoryandSubCategory] =
    useState({
      category: [],
      subCategory: [],
    });
  const [isLoading, setIsLoading] = useState(true);
  const roles = ["admin", "non_admin"];
  const clientQuery = clientID ? `&contact_id=${clientID}` : "";

  const [data, setParams, loading] = useFetchFilters(
    `${baseUrl}${apiUrl}?isAdmin=${is_admin}&userId=${id}${clientQuery}`,
  );
  const [userList, setUserList] = useState([]);
  const fetchUsers = async () => {
    try {
      const response = await getAllAssignUsers({ token });
      setUserList(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      setUserList([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignedUser = useMemo(() => {
    return Array.isArray(userList) && userList.length > 0
      ? [...new Set(userList.map((item) => item.user_name))]
      : [];
  }, [userList]);

  const CustomSelect = ({ children, ...props }) => (
    <select {...props} className="m-1 px-2 py-1 text-sm border rounded-md">
      {children}
    </select>
  );

  const isFiltered = table.getState().columnFilters.length > 0;

  const document_types = [
    "pdf",
    "doc",
    "docx",
    "jpeg",
    "jpg",
    "jfif",
    "png",
    "gif",
    "bmp",
    "heic",
    "heif",
    "tif",
    "tiff",
    "webp",
    "ico",
    "txt",
    "csv",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "mp4",
    "mov",
    "3gp",
    "wmv",
    "m4a",
    "aac",
    "amr",
    "mp3",
    "opus",
    "wav",
    "aiff",
    "flac",
    "alac",
    "ogg",
    "wma",
    "midi",
    "mp1",
    "mp2",
    "mpa",
    "mpeg",
    "mpg",
    "zip",
    "rar",
    "bin",
    "crdownload",
    "rtf",
    "html",
    "htm",
    "mhtml",
    "eml",
    "pages",
    "vcf",
    "ics",
    "url",
    "one",
    "msg",
  ];

  const document_status = [
    "Partially Uploaded",
    "Uploaded",
    "Requested",
    "Rejected",
  ];
  const status = ["Active", "Inactive"];
  const request_type = ["Required", "Recommended"];
  const field_type = [
    "client_type",
    "client_subtype",
    "document_type",
    "document_subtype",
  ];
  const source_type = ["DocUploader", "Email", "Fax", "Scanned"];
  const clientTypes = ["primary", "beneficiary"];

  const filterColumns = table
    .getAllColumns()
    .filter((column) => column.columnDef?.filterable)
    .map((column) => ({
      id: column.id,
      displayName: column.columnDef.displayName,
    }));

  // Created At & Modified At filtering states

  const handleResetFilter = () => {
    table.resetColumnFilters();
    setParams({});
    if (setCreatedAt) {
      setCreatedAt(null);
    }

    if (setUpdatedAt) {
      setUpdatedAt(null);
    }
  };

  // Sets createdAt to the selected date (end of day) or null.
  const handleCreatedAtSelect = (date) => {
    if (date) {
      const createdAt = new Date(date);
      createdAt.setHours(23, 59, 59);
      setCreatedAt(createdAt);
    } else {
      setCreatedAt(null);
    }
  };

  // Sets updatedAt to the selected date (end of day) or null.
  const handleUpdatedAtSelect = (date) => {
    if (date) {
      const updatedAt = new Date(date);
      updatedAt.setHours(23, 59, 59);
      setUpdatedAt(updatedAt);
    } else {
      setUpdatedAt(null);
    }
  };

  const fetchCategory = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(
        `${baseUrl}/api/get-document-filter-name?${is_admin}=1&userId=${id}`,
      );

      const data = await res.json();

      setCategory(data.category);
      setSubCategory(data.subcategory);
    } catch (err) {
    } finally {
      // setIsLoading(false);
    }
  };

  const fetchClientCategoryAndSubCategory = async () => {
    try {
      // setIsLoading(true);
      const res = await fetch(
        `${baseUrl}/api/get-client-filter-name?${is_admin}=1&userId=${id}`,
      );

      const data = await res.json();

      setClientCategoryandSubCategory({
        category: [...data.clientSubType],
        subCategory: [...data.clientType],
      });
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  // DataToolbar is only mounted when the filter panel is open, so fetching
  // on mount here is intentional. We use a ref guard to prevent double-fetching
  // if the component re-renders while a fetch is already in-flight.
  const hasFetched = React.useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCategory();
    fetchClientCategoryAndSubCategory();
  }, []);

  return (
    <div className="my-2 flex gap-2 items-center flex-wrap">
      <p className="text-sm text-muted-foreground uppercase">Filter by</p>

      {filterColumns?.map((column) => {
        let columnOptions = data[column?.id] || [];
        if (column?.id === "doc_category") columnOptions = category;
        if (column?.id === "fields_name")
          columnOptions = clientCategoryandSubCategory.category;
        if (column?.id === "client_subtype")
          columnOptions = clientCategoryandSubCategory.subCategory;
        if (column?.id === "document_subtype") columnOptions = subCategory;
        if (column?.id === "is_admin") columnOptions = roles;
        if (column?.id === "document_type") columnOptions = document_types;
        if (column?.id === "case_client_type")
          columnOptions = clientCategoryandSubCategory.category;
        if (column?.id === "case_sub_type")
          columnOptions = clientCategoryandSubCategory.subCategory;
        if (column?.id === "document_status") columnOptions = document_status;
        if (column?.id === "fields_tab_type") columnOptions = field_type;
        if (column?.id === "document_source_type") columnOptions = source_type;
        if (column?.id === "contact_type") columnOptions = clientTypes;
        if (column?.id === "status") columnOptions = status;
        if (column?.id === "request_type") columnOptions = request_type;
        if (column?.id === "category_name") columnOptions = clientCategories;
        if (column?.id === "assign_user") columnOptions = assignedUser;

        return (
          <DataTableFacetedFilter
            key={column.id}
            column={table.getColumn(column.id)}
            title={column.displayName}
            table={table}
            options={columnOptions}
            setParams={setParams}
            loading={loading}
            isLoading={isLoading}
          />
        );
      })}

      {
        // Date filters for "Created At" and "Modified At" using a calendar popover.

        <div className="flex items-center gap-2">
          {/* Created At Filter */}
          {setCreatedAt && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 px-2"
                >
                  <CalendarIcon className="w-4 h-4" />
                  {createdAt ? (
                    <span className="text-xs">
                      {format(createdAt, "yyyy-MM-dd")}
                    </span>
                  ) : (
                    <span className="text-xs">
                      {createdAtLabel ? createdAtLabel : "Created At"}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start">
                {/* <Calendar
                  mode="single"
                  selected={createdAt}
                  onSelect={handleCreatedAtSelect}
                  disabled={(date) => date > new Date()}
                /> */}
                <DayPicker
                  mode="single"
                  selected={createdAt}
                  onSelect={handleCreatedAtSelect}
                  captionLayout="dropdown"
                  fromYear={1970}
                  toYear={new Date().getFullYear()}
                  disabled={(date) => date > new Date()}
                  classNames={{
                    months: "flex justify-center",
                    month: "space-y-2",
                    table: "w-full border-collapse",
                    head: "text-gray-500 text-xs",
                    head_row: "flex justify-center gap-[2px]",
                    head_cell: "w-9 h-9 flex items-center justify-center",
                    row: "flex justify-center gap-[2px]",
                    cell: "w-9 h-9 p-0 text-center align-middle",
                    day: "w-9 h-9 flex items-center justify-center rounded hover:bg-gray-100 text-sm",
                    day_selected: "bg-blue-600 text-white hover:bg-blue-700",
                    day_today: "border border-blue-500",
                  }}
                  components={{
                    Dropdown: CustomSelect, // ✅ this applies to both month and year <select>
                  }}
                />
              </PopoverContent>
            </Popover>
          )}

          {/* Modified At Filter */}
          {setUpdatedAt && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 px-2"
                >
                  <CalendarIcon className="w-4 h-4" />
                  {updatedAt ? (
                    <span className="text-xs">
                      {format(updatedAt, "yyyy-MM-dd")}
                    </span>
                  ) : (
                    <span className="text-xs">
                      {UpdatedAtLabel ? UpdatedAtLabel : "Modified At"}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start">
                {/* <Calendar
                  mode="single"
                  selected={updatedAt}
                  onSelect={handleUpdatedAtSelect}
                  disabled={(date) => date > new Date()}
                /> */}

                <DayPicker
                  mode="single"
                  selected={updatedAt}
                  onSelect={handleUpdatedAtSelect}
                  captionLayout="dropdown"
                  fromYear={1970}
                  toYear={new Date().getFullYear()}
                  disabled={(date) => date > new Date()}
                  classNames={{
                    months: "flex justify-center",
                    month: "space-y-2",
                    table: "w-full border-collapse",
                    head: "text-gray-500 text-xs",
                    head_row: "flex justify-center gap-[2px]",
                    head_cell: "w-9 h-9 flex items-center justify-center",
                    row: "flex justify-center gap-[2px]",
                    cell: "w-9 h-9 p-0 text-center align-middle",
                    day: "w-9 h-9 flex items-center justify-center rounded hover:bg-gray-100 text-sm",
                    day_selected: "bg-blue-600 text-white hover:bg-blue-700",
                    day_today: "border border-blue-500",
                  }}
                  components={{
                    Dropdown: CustomSelect, // ✅ this applies to both month and year <select>
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      }

      {(isFiltered || createdAt || updatedAt) && (
        <Button
          variant="ghost"
          onClick={handleResetFilter}
          className="h-8 px-2 lg:px-3"
        >
          Reset
          <Cross2Icon className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default DataToolbar;
